import type { NavigationState, PartialState } from '@react-navigation/native';

import { telemetry } from './telemetry';
import type { TelemetryTab } from './telemetryTypes';

const MAIN_TABS = new Set<TelemetryTab>(['Home', 'Spend', 'Wealth', 'Grow', 'Loan']);

type NavState = NavigationState | PartialState<NavigationState> | undefined;

function activeRouteChain(state: NavState): string[] {
  const names: string[] = [];
  let current = state;
  while (current?.routes && typeof current.index === 'number') {
    const route = current.routes[current.index];
    if (!route?.name) break;
    names.push(String(route.name));
    current = route.state;
  }
  return names;
}

function tabFromChain(chain: string[]): TelemetryTab | undefined {
  return chain.find((name): name is TelemetryTab => MAIN_TABS.has(name as TelemetryTab));
}

/**
 * Logs `screen_viewed` + `tab_view` when the active route or tab changes.
 * Call from both native and web NavigationContainer.
 */
export function trackNavigationState(
  state: NavigationState | undefined,
  lastRouteRef: { current: string },
  lastTabRef: { current: TelemetryTab | undefined },
): void {
  const chain = activeRouteChain(state);
  const leaf = chain[chain.length - 1];
  if (!leaf) return;

  const key = chain.join('/');
  if (key === lastRouteRef.current) return;
  lastRouteRef.current = key;

  const tab = tabFromChain(chain);
  void telemetry.logScreenView(leaf, tab);

  if (tab && tab !== lastTabRef.current) {
    lastTabRef.current = tab;
    void telemetry.logTabView(tab);
  }
}
